import type { Analytics } from 'firebase/analytics';
import { getAnalytics } from 'firebase/analytics';
import { getApp, getApps, initializeApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import { FacebookAuthProvider, getAuth, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import type { FirebasePerformance } from 'firebase/performance';
import { getPerformance, initializePerformance } from 'firebase/performance';
import type { RemoteConfig } from 'firebase/remote-config';
import { getRemoteConfig } from 'firebase/remote-config';

import { getBackendConfig } from '../utils/backends';

// Initialize Firebase app
const app = getApps().length ? getApp() : initializeApp(getBackendConfig('firebaseConfig'));

// Initialize Performance Monitoring
initializePerformance(app);

// Initialize Firebase services
let authInstance = getAuth(app);
let analyticsInstance = getAnalytics(app);
let performanceInstance = getPerformance(app);
let remoteConfigInstance = getRemoteConfig(app);
let firestoreInstance: Firestore | undefined;

// Exported functions for accessing and setting instances
export const getAuthInstance = (): Auth => authInstance;
export const setAuthInstance = (instance: Auth): void => {
  authInstance = instance;
};

export const getAnalyticsInstance = (): Analytics => analyticsInstance;
export const setAnalyticsInstance = (instance: Analytics) => {
  analyticsInstance = instance;
};

export const getPerformanceInstance = (): FirebasePerformance => performanceInstance;
export const setPerformanceInstance = (instance: FirebasePerformance) => {
  performanceInstance = instance;
};

export const getRemoteConfigInstance = (): RemoteConfig => remoteConfigInstance;
export const setRemoteConfigInstance = (instance: RemoteConfig) => {
  remoteConfigInstance = instance;
};
if (remoteConfigInstance) {
  remoteConfigInstance.settings.minimumFetchIntervalMillis =
    process.env.NODE_ENV === 'development' ? 0 : 60 * 60 * 1000;
}

export const getFirestoreInstance = async (): Promise<Firestore> => {
  if (!firestoreInstance) firestoreInstance = getFirestore(app);
  return firestoreInstance;
};
export const setFirestoreInstance = (instance: Firestore) => {
  firestoreInstance = instance;
};

// Initialize authentication providers
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
export const appleProvider = new OAuthProvider('apple.com');

export const restName = 'firebase-mobile';

export default app;
